import { useEffect, useRef, useState } from 'react';
import { wsClient } from './ws-client';
import { useQuoteStore, type LiveQuote } from '@/stores/quotes';

/**
 * 订阅一组品种的实时行情(quote 频道),每帧写进 quote store。组件卸载自动退订。
 *
 * @param symbolIds 要订阅的品种数字 ID 列表(字符串)。空数组时不订阅。
 *
 * 用法:`useQuoteStream(['41'])` 订阅 XAUUSD,再用 `useLiveQuote('41')` 读最新行情。
 * 多个组件订阅同一品种共享底层订阅,引用计数归零时才向后端 unsubscribe。
 */
export function useQuoteStream(symbolIds: string[]): void {
  // 用排序去重后的 join 作为依赖键,避免每次渲染新数组引用导致反复重订阅。
  const key = Array.from(new Set(symbolIds)).sort().join(',');
  useEffect(() => {
    if (!key) return;
    const ids = key.split(',');
    const apply = useQuoteStore.getState().applyQuote;
    const unsub = wsClient.subscribeQuote(ids, apply);
    return unsub;
  }, [key]);
}

/** 读单个品种的最新行情(随 tick rerender);无行情时返回 undefined。 */
export function useLiveQuote(symbolId: string | undefined): LiveQuote | undefined {
  return useQuoteStore((s) => (symbolId ? s.quotes[symbolId] : undefined));
}

/**
 * 价格跳动闪烁 — 监测一个数值,变大返回 'up'、变小返回 'down',约 [ms] 后归零。
 * 配合背景色/文字色做 cTrader 风格的逐 tick 红绿闪烁。
 */
export function usePriceFlash(value: number | undefined, ms = 350): 'up' | 'down' | null {
  const prevRef = useRef<number | undefined>(undefined);
  const [flash, setFlash] = useState<'up' | 'down' | null>(null);
  useEffect(() => {
    if (value == null) return;
    const prev = prevRef.current;
    prevRef.current = value;
    if (prev == null || value === prev) return;
    setFlash(value > prev ? 'up' : 'down');
    const t = setTimeout(() => setFlash(null), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return flash;
}
